import { useEffect, useRef, useState } from "react";
import { useReactiveVar } from "@apollo/client";
import { GetCompanyAndRoleQuery, GetCompanyAndRoleWithoutOwnerQuery } from "../../main/company/graphql/company.hooks";
import { currentBranchEmployeeIdVar, roleAccessReloadVar } from "../../main/globalVar/state";
import { useGetRoleAccessListQuery } from "../../main/roleAccesses/graphql/roleAccesses.hooks";
import AccessService from "../../services/AccessService";

type CurrentBranchAccessUserData = GetCompanyAndRoleQuery | GetCompanyAndRoleWithoutOwnerQuery | undefined;

interface UseCurrentBranchAccessParams {
    branchId: number | null;
    isCreateOwnerPage: boolean;
    userData: CurrentBranchAccessUserData;
    userDataLoading: boolean;
}

export const useCurrentBranchAccess = ({
    branchId,
    isCreateOwnerPage,
    userData,
    userDataLoading,
}: UseCurrentBranchAccessParams) => {
    const roleAccessReloadRequest = useReactiveVar(roleAccessReloadVar);
    const lastHandledRoleAccessReloadRequestRef = useRef<number>(roleAccessReloadRequest);
    const [isAccessReady, setAccessReady] = useState<boolean>(false);
    const [roleAccessVersion, setRoleAccessVersion] = useState<number | null>(null);

    const shouldRunRoleAccessFlow = !isCreateOwnerPage && !userDataLoading && !!userData && branchId !== null;
    const currentBranchRole = userData?.user?.employee?.userBranchRelations?.[0]?.role ?? null;
    const resolvedCurrentBranchRoleId = currentBranchRole?.id ?? null;

    const {
        data: roleAccessListData,
        loading: roleAccessListLoading,
        refetch: refetchRoleAccessList,
    } = useGetRoleAccessListQuery({
        skip: !shouldRunRoleAccessFlow || !resolvedCurrentBranchRoleId || branchId === null,
        variables: {
            branchId: branchId!,
            roleId: resolvedCurrentBranchRoleId!,
        },
        fetchPolicy: "network-only",
        context: { errorType: "global" },
    });

    useEffect(() => {
        if (!userData) return;

        const accessServiceInstance = AccessService.getInstance();
        accessServiceInstance.init(userData?.user?.company?.isPremiumOn);
        accessServiceInstance.setModules(userData?.user?.company?.modules);
        accessServiceInstance.setBookingModerator(userData?.user?.roles);
        currentBranchEmployeeIdVar(userData?.user?.employee?.id);

        if (isCreateOwnerPage) {
            setAccessReady(true);
        }
    }, [isCreateOwnerPage, userData, userDataLoading]);

    // reset/start the entire branch access session and add accessVersion increment
    useEffect(() => {
        if (!shouldRunRoleAccessFlow) {
            setRoleAccessVersion(null);
            if (!isCreateOwnerPage) {
                setAccessReady(false);
            }
            return;
        }

        const accessServiceInstance = AccessService.getInstance();
        const nextRoleAccessVersion = accessServiceInstance.beginRoleAccessReload(branchId);

        setRoleAccessVersion(nextRoleAccessVersion);
        setAccessReady(false);
    }, [branchId, isCreateOwnerPage, resolvedCurrentBranchRoleId, shouldRunRoleAccessFlow]);

    useEffect(() => {
        if (!shouldRunRoleAccessFlow || roleAccessVersion === null || branchId === null) return;
        const accessServiceInstance = AccessService.getInstance();
        // Set the user's role in the current branch
        accessServiceInstance.setCurrentBranchRole(currentBranchRole, branchId, roleAccessVersion);
        if (!resolvedCurrentBranchRoleId) {
            setAccessReady(accessServiceInstance.isRoleAccessReady());
        }
    }, [branchId, currentBranchRole, resolvedCurrentBranchRoleId, roleAccessVersion, shouldRunRoleAccessFlow]);

    useEffect(() => {
        if (roleAccessReloadRequest === lastHandledRoleAccessReloadRequestRef.current) return;
        if (!shouldRunRoleAccessFlow || !resolvedCurrentBranchRoleId || branchId === null) return;

        lastHandledRoleAccessReloadRequestRef.current = roleAccessReloadRequest;

        let isCancelled = false;

        const reloadCurrentRoleAccess = async () => {
            const accessServiceInstance = AccessService.getInstance();
            const nextRoleAccessVersion = accessServiceInstance.beginRoleAccessReload(branchId);

            setAccessReady(false);

            try {
                const { data } = await refetchRoleAccessList({
                    branchId,
                    roleId: resolvedCurrentBranchRoleId,
                });

                if (isCancelled) return;

                accessServiceInstance.setCurrentBranchRole(currentBranchRole, branchId, nextRoleAccessVersion);
                accessServiceInstance.setCurrentRoleAccessList(data?.roleAccessList ?? [], {
                    branchId,
                    roleId: resolvedCurrentBranchRoleId,
                    version: nextRoleAccessVersion,
                });
                setRoleAccessVersion(nextRoleAccessVersion);
                setAccessReady(accessServiceInstance.isRoleAccessReady());
            } catch (error) {
                if (!isCancelled) {
                    console.error(error);
                }
            }
        };

        void reloadCurrentRoleAccess();

        return () => {
            isCancelled = true;
        };
    }, [
        branchId,
        currentBranchRole,
        refetchRoleAccessList,
        resolvedCurrentBranchRoleId,
        roleAccessReloadRequest,
        shouldRunRoleAccessFlow,
    ]);

    useEffect(() => {
        if (
            !shouldRunRoleAccessFlow ||
            roleAccessVersion === null ||
            !resolvedCurrentBranchRoleId ||
            roleAccessListLoading ||
            branchId === null
        ) {
            return;
        }

        const accessServiceInstance = AccessService.getInstance();

        accessServiceInstance.setCurrentRoleAccessList(roleAccessListData?.roleAccessList ?? [], {
            branchId,
            roleId: resolvedCurrentBranchRoleId,
            version: roleAccessVersion,
        });
        setAccessReady(accessServiceInstance.isRoleAccessReady());
    }, [
        branchId,
        resolvedCurrentBranchRoleId,
        roleAccessVersion,
        roleAccessListData,
        roleAccessListLoading,
        shouldRunRoleAccessFlow,
    ]);

    return { isAccessReady };
};
